/**
 * Purchase-order editor + lifecycle — `/purchasing/purchase-orders/new` and
 * `/purchasing/purchase-orders/:id`.
 *
 * Covers create (POST, Idempotency-Key), edit (PUT, If-Match), delete draft,
 * the pre-submit approval preview, and every published transition: submit,
 * send (with a recipient override + resend log) and close. Quantities, unit
 * costs and amounts are exact decimal strings from wire to wire; the client
 * totals are a preview only — the server's recalculated totals win on save.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Info, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@erp/ui";

import { ConflictDialog } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useMutation } from "@tanstack/react-query";

import {
  closePurchaseOrder,
  createPurchaseOrder,
  deletePurchaseOrder,
  sendPurchaseOrder,
  submitPurchaseOrder,
  updatePurchaseOrder,
  RECEIVABLE_STATUSES,
  type PurchaseOrder,
  type PurchaseOrderCreate,
  type PurchaseOrderLineInput,
  type PurchaseOrderUpdate,
  type SupplierItem,
} from "../api";
import {
  purchasingKeys,
  useApprovalPreview,
  usePurchaseOrderDetail,
  usePurchasingConfig,
  useSupplierItemsBySupplier,
  useTransmissions,
} from "../queries";
import { useSupplierNames, useUomCodes, useVariantSkus } from "../components/lookup-names";
import { DecimalInput } from "../components/decimal-input";
import { PurchaseOrderStatusBadge, TransmissionStatusBadge } from "../components/status-badges";
import { handleProblem, type FieldErrors } from "../problem-errors";
import { enumLabel } from "../labels";
import {
  AMOUNT_SCALE,
  COST_SCALE,
  QTY_SCALE,
  addDecimals,
  compareDecimal,
  multiplyDecimal,
  subtractDecimal,
  toScale,
} from "../decimal";

interface LineDraft {
  /** Stable client key; server line ids are not reused as React keys. */
  key: string;
  supplier_item_id?: string;
  variant_id: string;
  purchase_uom_id: string;
  supplier_sku: string;
  item_reference_code: string;
  qty_ordered: string;
  unit_cost: string;
  discount_amount: string;
  tax_amount: string;
  expected_delivery_date: string;
  unit_weight: string;
  over_receipt_tolerance_pct: string;
}

interface HeaderDraft {
  supplier_id: string;
  currency: string;
  order_date: string;
  expected_delivery_date: string;
  supplier_reference_token: string;
  shipping_total: string;
  reapproval_tolerance_amount: string;
}

const ZERO_AMOUNT = "0.0000";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Lead time is a whole number of days — never a decimal wire value. */
function addDays(isoDate: string, days: number): string {
  const base = new Date(`${isoDate}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

let lineKeySeed = 0;
function newLineKey(): string {
  lineKeySeed += 1;
  return `line-${lineKeySeed}`;
}

function emptyLine(purchaseUomId: string): LineDraft {
  return {
    key: newLineKey(),
    variant_id: "",
    purchase_uom_id: purchaseUomId,
    supplier_sku: "",
    item_reference_code: "",
    qty_ordered: "",
    unit_cost: "",
    discount_amount: "",
    tax_amount: "",
    expected_delivery_date: "",
    unit_weight: "",
    over_receipt_tolerance_pct: "",
  };
}

const EDITABLE_STATUSES = new Set(["draft", "pending_approval", "approved"]);

export interface PoEditorPageProps {
  /** Undefined renders the create form. */
  purchaseOrderId?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canSubmit?: boolean;
  canSend?: boolean;
  canClose?: boolean;
  canReceive?: boolean;
  canReadTransmissions?: boolean;
  canReadSuppliers?: boolean;
  canReadVariants?: boolean;
  canReadUoms?: boolean;
  canReadConfig?: boolean;
  onCreated: (purchaseOrderId: string) => void;
  onDeleted: () => void;
  onOpenReceive: (purchaseOrderId: string) => void;
  onOpenGrns: (purchaseOrderId: string) => void;
}

export function PoEditorPage({
  purchaseOrderId,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  canSubmit = false,
  canSend = false,
  canClose = false,
  canReceive = false,
  canReadTransmissions = false,
  canReadSuppliers = true,
  canReadVariants = true,
  canReadUoms = true,
  canReadConfig = true,
  onCreated,
  onDeleted,
  onOpenReceive,
  onOpenGrns,
}: PoEditorPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = purchaseOrderId === undefined;

  const detail = usePurchaseOrderDetail(purchaseOrderId);
  const order = detail.data?.data;
  const etagRef = useRef<string | null>(null);

  const config = usePurchasingConfig(canReadConfig);
  const { parties } = useSupplierNames(canReadSuppliers);
  const { variants } = useVariantSkus(canReadVariants);
  const { uoms, uomCode } = useUomCodes(canReadUoms);

  const [header, setHeader] = useState<HeaderDraft>({
    supplier_id: "",
    currency: "SAR",
    order_date: todayIso(),
    expected_delivery_date: "",
    supplier_reference_token: "",
    shipping_total: "",
    reapproval_tolerance_amount: "",
  });
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [conflict, setConflict] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [reapprovalNotice, setReapprovalNotice] = useState<{ from: string; to: string } | null>(
    null,
  );

  const supplierItems = useSupplierItemsBySupplier(
    header.supplier_id === "" ? undefined : header.supplier_id,
  );
  const supplierItemFor = (variantId: string): SupplierItem | undefined =>
    supplierItems.data?.find((item) => item.variant_id === variantId);

  const defaultUomId = uoms[0]?.id ?? "";

  // Hydrate the form from the loaded PO (and re-hydrate after every save).
  useEffect(() => {
    if (order === undefined) return;
    etagRef.current = detail.data?.etag ?? null;
    setHeader({
      supplier_id: order.supplier_id ?? "",
      currency: order.currency,
      order_date: order.order_date,
      expected_delivery_date: order.expected_delivery_date ?? "",
      supplier_reference_token: order.supplier_reference_token ?? "",
      shipping_total: order.shipping_total,
      reapproval_tolerance_amount: "",
    });
    setLines(
      order.lines.map((line) => ({
        key: newLineKey(),
        ...(line.supplier_item_id === undefined ? {} : { supplier_item_id: line.supplier_item_id }),
        variant_id: line.variant_id,
        purchase_uom_id: line.purchase_uom_id,
        supplier_sku: line.supplier_sku,
        item_reference_code: line.item_reference_code,
        qty_ordered: line.qty_ordered,
        unit_cost: line.unit_cost,
        discount_amount: line.discount_amount,
        tax_amount: line.tax_amount,
        expected_delivery_date: line.expected_delivery_date ?? "",
        unit_weight: line.unit_weight ?? "",
        over_receipt_tolerance_pct: line.over_receipt_tolerance_pct,
      })),
    );
  }, [order, detail.data?.etag]);

  // A brand-new PO starts with one blank line so the grid is never empty.
  useEffect(() => {
    if (isNew && lines.length === 0 && defaultUomId !== "") {
      setLines([emptyLine(defaultUomId)]);
    }
  }, [isNew, lines.length, defaultUomId]);

  const status = order?.status ?? "draft";
  const editable = isNew ? canCreate : canUpdate && EDITABLE_STATUSES.has(status);
  const isApprovedEdit = !isNew && (status === "approved" || status === "sent");

  const approvalPreview = useApprovalPreview(
    purchaseOrderId,
    !isNew && (status === "draft" || status === "pending_approval"),
  );
  const transmissions = useTransmissions(purchaseOrderId, !isNew && canReadTransmissions);

  // ── Client-side total preview (server value is authoritative) ──────────
  const lineSubtotal = (line: LineDraft): string => {
    if (line.qty_ordered === "" || line.unit_cost === "") return ZERO_AMOUNT;
    return multiplyDecimal(line.qty_ordered, QTY_SCALE, line.unit_cost, COST_SCALE, AMOUNT_SCALE);
  };
  const lineTotal = (line: LineDraft): string => {
    const gross = subtractDecimal(
      lineSubtotal(line),
      line.discount_amount === "" ? ZERO_AMOUNT : line.discount_amount,
      AMOUNT_SCALE,
    );
    return addDecimals(
      [gross, line.tax_amount === "" ? ZERO_AMOUNT : line.tax_amount],
      AMOUNT_SCALE,
    );
  };
  const previewSubtotal = addDecimals(lines.map(lineSubtotal), AMOUNT_SCALE);
  const previewGrandTotal = addDecimals(
    [...lines.map(lineTotal), header.shipping_total === "" ? ZERO_AMOUNT : header.shipping_total],
    AMOUNT_SCALE,
  );

  const updateLine = (key: string, patch: Partial<LineDraft>): void => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /** Supplier-item defaulting on variant pick (SPEC §PO editor). */
  const applySupplierDefaults = (key: string, variantId: string): void => {
    const item = supplierItemFor(variantId);
    const variant = variants.find((candidate) => candidate.id === variantId);
    const patch: Partial<LineDraft> = {
      variant_id: variantId,
      item_reference_code: variant?.sku ?? "",
    };
    if (item !== undefined) {
      patch.supplier_item_id = item.id;
      patch.supplier_sku = item.supplier_sku;
      patch.over_receipt_tolerance_pct = item.over_receipt_tolerance_pct;
      if (item.last_cost !== undefined) patch.unit_cost = item.last_cost;
      patch.expected_delivery_date = addDays(
        header.order_date === "" ? todayIso() : header.order_date,
        item.lead_time_days,
      );
    }
    updateLine(key, patch);
  };

  const buildLineInputs = (): PurchaseOrderLineInput[] =>
    lines.map((line, index) => ({
      line_number: index + 1,
      ...(line.supplier_item_id === undefined ? {} : { supplier_item_id: line.supplier_item_id }),
      variant_id: line.variant_id,
      purchase_uom_id: line.purchase_uom_id,
      supplier_sku: line.supplier_sku,
      item_reference_code: line.item_reference_code,
      qty_ordered: toScale(line.qty_ordered === "" ? "0" : line.qty_ordered, QTY_SCALE),
      unit_cost: toScale(line.unit_cost === "" ? "0" : line.unit_cost, COST_SCALE),
      ...(line.discount_amount === ""
        ? {}
        : { discount_amount: toScale(line.discount_amount, AMOUNT_SCALE) }),
      ...(line.tax_amount === "" ? {} : { tax_amount: toScale(line.tax_amount, AMOUNT_SCALE) }),
      ...(line.expected_delivery_date === ""
        ? {}
        : { expected_delivery_date: line.expected_delivery_date }),
      ...(line.unit_weight === "" ? {} : { unit_weight: toScale(line.unit_weight, QTY_SCALE) }),
      ...(line.over_receipt_tolerance_pct === ""
        ? {}
        : { over_receipt_tolerance_pct: line.over_receipt_tolerance_pct }),
    }));

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: purchasingKeys.purchaseOrders.all });
  };

  const onMutationError = (error: unknown): void => {
    handleProblem(error, t, {
      onConflict: () => setConflict(true),
      onFieldErrors: setFieldErrors,
    });
  };

  const createMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof createPurchaseOrder>>,
    PurchaseOrderCreate
  >({
    mutationFn: (input, key) => createPurchaseOrder(input, key),
    onSuccess: ({ data }) => {
      setFieldErrors({});
      invalidate();
      onCreated(data.id);
    },
    onError: onMutationError,
  });

  const saveMutation = useMutation({
    mutationFn: (input: PurchaseOrderUpdate) =>
      updatePurchaseOrder(purchaseOrderId as string, input, etagRef.current ?? ""),
    onSuccess: ({ data, etag }) => {
      etagRef.current = etag;
      setFieldErrors({});
      if (isApprovedEdit && data.status === "pending_approval" && order !== undefined) {
        // The tolerance flip returned the PO to approval — name both totals.
        setReapprovalNotice({ from: order.grand_total, to: data.grand_total });
      }
      invalidate();
    },
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePurchaseOrder(purchaseOrderId as string),
    onSuccess: () => {
      invalidate();
      onDeleted();
    },
    onError: onMutationError,
  });

  const submitMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof submitPurchaseOrder>>,
    void
  >({
    mutationFn: (_variables, key) =>
      submitPurchaseOrder(purchaseOrderId as string, etagRef.current ?? "", key),
    onSuccess: ({ data, etag }) => {
      etagRef.current = etag;
      invalidate();
      toast({
        title:
          data.status === "approved"
            ? t("purchasing.poEditor.autoApproved", "Auto-approved — below the approval threshold.")
            : t("purchasing.poEditor.submitted", "Submitted for approval."),
      });
    },
    onError: onMutationError,
  });

  const sendMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof sendPurchaseOrder>>,
    { recipient_email?: string }
  >({
    mutationFn: (input, key) =>
      sendPurchaseOrder(purchaseOrderId as string, input, etagRef.current ?? "", key),
    onSuccess: ({ etag }) => {
      etagRef.current = etag;
      setSendOpen(false);
      setRecipient("");
      invalidate();
      void queryClient.invalidateQueries({
        queryKey: purchasingKeys.purchaseOrders.transmissions(purchaseOrderId ?? ""),
      });
    },
    onError: onMutationError,
  });

  const closeMutation = useIdempotentMutation<Awaited<ReturnType<typeof closePurchaseOrder>>, void>(
    {
      mutationFn: (_variables, key) =>
        closePurchaseOrder(purchaseOrderId as string, etagRef.current ?? "", key),
      onSuccess: ({ etag }) => {
        etagRef.current = etag;
        setCloseOpen(false);
        invalidate();
      },
      onError: onMutationError,
    },
  );

  const save = (): void => {
    setFieldErrors({});
    if (lines.length === 0) {
      setFieldErrors({ lines: t("purchasing.poEditor.linesRequired", "Add at least one line.") });
      return;
    }
    const lineInputs = buildLineInputs();
    if (isNew) {
      const body: PurchaseOrderCreate = {
        ...(header.supplier_id === "" ? {} : { supplier_id: header.supplier_id }),
        currency: header.currency.toUpperCase(),
        order_date: header.order_date,
        ...(header.expected_delivery_date === ""
          ? {}
          : { expected_delivery_date: header.expected_delivery_date }),
        ...(header.supplier_reference_token === ""
          ? {}
          : { supplier_reference_token: header.supplier_reference_token }),
        ...(header.shipping_total === ""
          ? {}
          : { shipping_total: toScale(header.shipping_total, AMOUNT_SCALE) }),
        ...(header.reapproval_tolerance_amount === ""
          ? {}
          : {
              reapproval_tolerance_amount: toScale(
                header.reapproval_tolerance_amount,
                AMOUNT_SCALE,
              ),
            }),
        lines: lineInputs,
      };
      createMutation.mutate(body);
      return;
    }
    const body: PurchaseOrderUpdate = {
      ...(header.supplier_id === "" ? {} : { supplier_id: header.supplier_id }),
      order_date: header.order_date,
      ...(header.expected_delivery_date === ""
        ? {}
        : { expected_delivery_date: header.expected_delivery_date }),
      ...(header.supplier_reference_token === ""
        ? {}
        : { supplier_reference_token: header.supplier_reference_token }),
      ...(header.shipping_total === ""
        ? {}
        : { shipping_total: toScale(header.shipping_total, AMOUNT_SCALE) }),
      ...(header.reapproval_tolerance_amount === ""
        ? {}
        : {
            reapproval_tolerance_amount: toScale(header.reapproval_tolerance_amount, AMOUNT_SCALE),
          }),
      lines: lineInputs,
    };
    saveMutation.mutate(body);
  };

  const supplierOptions = useMemo(
    () => parties.map((party) => ({ value: party.id, label: party.display_name })),
    [parties],
  );
  const variantOptions = useMemo(
    () => variants.map((variant) => ({ value: variant.id, label: variant.sku })),
    [variants],
  );

  const busy =
    createMutation.isPending ||
    saveMutation.isPending ||
    submitMutation.isPending ||
    sendMutation.isPending ||
    closeMutation.isPending ||
    deleteMutation.isPending;

  const renderQty = (value: string, digits = QTY_SCALE): string => {
    try {
      return formatQuantity(value, i18n.language, digits);
    } catch {
      return value;
    }
  };

  if (!isNew && detail.isLoading) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (!isNew && detail.isError) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("purchasing.poEditor.loadError", "This purchase order could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            {isNew ? (
              t("purchasing.poEditor.newTitle", "New purchase order")
            ) : (
              <>
                <span dir="ltr" className="font-mono">
                  {order?.po_number}
                </span>
                <PurchaseOrderStatusBadge status={status} />
              </>
            )}
          </h1>
          {!isNew && order !== undefined ? (
            <p className="text-sm text-muted-foreground">
              {t("purchasing.poEditor.totalLine", "Total")}{" "}
              <MoneyDisplay value={{ amount: order.grand_total, currency: order.currency }} />
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editable ? (
            <Button
              type="button"
              onClick={save}
              loading={createMutation.isPending || saveMutation.isPending}
              disabled={busy}
            >
              {isNew
                ? t("purchasing.poEditor.createDraft", "Create draft")
                : t("purchasing.poEditor.saveChanges", "Save changes")}
            </Button>
          ) : null}
          {!isNew && canSubmit && status === "draft" ? (
            <Button
              type="button"
              variant="secondary"
              loading={submitMutation.isPending}
              disabled={busy}
              onClick={() => submitMutation.mutate()}
            >
              {t("purchasing.poEditor.submit", "Submit for approval")}
            </Button>
          ) : null}
          {!isNew && canSend && (status === "approved" || status === "sent") ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setSendOpen(true)}
            >
              {status === "sent"
                ? t("purchasing.poEditor.resend", "Resend to supplier")
                : t("purchasing.poEditor.send", "Send to supplier")}
            </Button>
          ) : null}
          {!isNew && canReceive && RECEIVABLE_STATUSES.includes(status) ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenReceive(purchaseOrderId as string)}
            >
              {t("purchasing.poEditor.receive", "Receive goods")}
            </Button>
          ) : null}
          {!isNew && canClose && status === "received" ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setCloseOpen(true)}
            >
              {t("purchasing.poEditor.close", "Close purchase order")}
            </Button>
          ) : null}
          {!isNew && canDelete && status === "draft" ? (
            <Button
              type="button"
              variant="ghost"
              loading={deleteMutation.isPending}
              disabled={busy}
              onClick={() => deleteMutation.mutate()}
            >
              {t("purchasing.poEditor.deleteDraft", "Delete draft")}
            </Button>
          ) : null}
        </div>
      </div>

      {isApprovedEdit && canUpdate ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            {t(
              "purchasing.poEditor.reapprovalWarning",
              "Edits that raise the total beyond the configured tolerance return this order to pending approval and notify approvers.",
            )}
            {config.data !== undefined ? (
              <>
                {" "}
                <MoneyDisplay
                  value={{
                    amount: config.data.reapproval_tolerance_amount,
                    currency: header.currency,
                  }}
                />
              </>
            ) : null}
          </span>
        </p>
      ) : null}

      {!isNew && !editable ? (
        <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t(
            "purchasing.poEditor.readOnly",
            "This purchase order is read-only in its current status.",
          )}
        </p>
      ) : null}

      {approvalPreview.data !== undefined ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-sm">
            <span className="flex items-center gap-2 font-medium">
              <Info className="size-4" aria-hidden="true" />
              {approvalPreview.data.evaluation === "auto_approve"
                ? t("purchasing.poEditor.autoApproveHint", "Auto-approves on submit")
                : t("purchasing.poEditor.approvalRequiredHint", "Approval required on submit")}
            </span>
            {approvalPreview.data.matched_threshold_amount !== undefined ? (
              <span className="text-muted-foreground">
                {t("purchasing.poEditor.threshold", "Threshold")}{" "}
                <MoneyDisplay
                  value={{
                    amount: approvalPreview.data.matched_threshold_amount,
                    currency: header.currency,
                  }}
                />
              </span>
            ) : null}
            {approvalPreview.data.required_approver_role_code !== null ? (
              <span className="text-muted-foreground">
                {t("purchasing.poEditor.approverRole", "Approver role")}{" "}
                <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {approvalPreview.data.required_approver_role_code}
                </code>
              </span>
            ) : null}
            <span className="text-muted-foreground">
              {t("purchasing.poEditor.rule", "Rule")}{" "}
              <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {approvalPreview.data.matched_threshold_rule_code}
              </code>
            </span>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.poEditor.headerTitle", "Order header")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-supplier-field">
              {t("purchasing.poEditor.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-po-supplier-field"
              options={supplierOptions}
              value={header.supplier_id === "" ? null : header.supplier_id}
              onChange={(value) => {
                const party = parties.find((candidate) => candidate.id === value);
                setHeader((current) => ({
                  ...current,
                  supplier_id: value ?? "",
                  currency:
                    isNew && party?.default_currency !== undefined
                      ? party.default_currency
                      : current.currency,
                }));
              }}
              disabled={!editable}
              placeholder={t("purchasing.common.pickSupplier", "Choose a supplier…")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
              aria-invalid={fieldErrors.supplier_id !== undefined}
            />
            {fieldErrors.supplier_id !== undefined ? (
              <p role="alert" className="text-xs text-destructive">
                {fieldErrors.supplier_id}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-currency">
              {t("purchasing.poEditor.currency", "Currency")}
            </Label>
            <Input
              id="purchasing-po-currency"
              dir="ltr"
              maxLength={3}
              value={header.currency}
              disabled={!isNew || !editable}
              onChange={(event) =>
                setHeader((current) => ({
                  ...current,
                  currency: event.target.value.toUpperCase(),
                }))
              }
              aria-invalid={fieldErrors.currency !== undefined}
            />
            {fieldErrors.currency !== undefined ? (
              <p role="alert" className="text-xs text-destructive">
                {fieldErrors.currency}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-order-date">
              {t("purchasing.poEditor.orderDate", "Order date")}
            </Label>
            <Input
              id="purchasing-po-order-date"
              type="date"
              dir="ltr"
              value={header.order_date}
              disabled={!editable}
              onChange={(event) =>
                setHeader((current) => ({ ...current, order_date: event.target.value }))
              }
              aria-invalid={fieldErrors.order_date !== undefined}
            />
            {fieldErrors.order_date !== undefined ? (
              <p role="alert" className="text-xs text-destructive">
                {fieldErrors.order_date}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-expected">
              {t("purchasing.poEditor.expectedDate", "Expected delivery")}
            </Label>
            <Input
              id="purchasing-po-expected"
              type="date"
              dir="ltr"
              value={header.expected_delivery_date}
              disabled={!editable}
              onChange={(event) =>
                setHeader((current) => ({
                  ...current,
                  expected_delivery_date: event.target.value,
                }))
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-reference">
              {t("purchasing.poEditor.supplierReference", "Supplier reference")}
            </Label>
            <Input
              id="purchasing-po-reference"
              dir="ltr"
              value={header.supplier_reference_token}
              disabled={!editable}
              onChange={(event) =>
                setHeader((current) => ({
                  ...current,
                  supplier_reference_token: event.target.value,
                }))
              }
              aria-invalid={fieldErrors.supplier_reference_token !== undefined}
            />
            {fieldErrors.supplier_reference_token !== undefined ? (
              <p role="alert" className="text-xs text-destructive">
                {fieldErrors.supplier_reference_token}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-shipping">
              {t("purchasing.poEditor.shippingTotal", "Shipping total")}
            </Label>
            <DecimalInput
              id="purchasing-po-shipping"
              scale={AMOUNT_SCALE}
              value={header.shipping_total}
              disabled={!editable}
              onChange={(next) => setHeader((current) => ({ ...current, shipping_total: next }))}
            />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="lines">
        <TabsList>
          <TabsTrigger value="lines">{t("purchasing.poEditor.tabs.lines", "Lines")}</TabsTrigger>
          {!isNew && canReadTransmissions ? (
            <TabsTrigger value="transmissions">
              {t("purchasing.poEditor.tabs.transmissions", "Transmissions")}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="lines" className="flex flex-col gap-3">
          {fieldErrors.lines !== undefined ? (
            <p role="alert" className="text-sm text-destructive">
              {fieldErrors.lines}
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[60rem] text-sm">
              <thead>
                <tr className="border-b text-start text-xs uppercase text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    #
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.poEditor.line.variant", "Variant")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.poEditor.line.supplierSku", "Supplier SKU")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.poEditor.line.uom", "UoM")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.poEditor.line.qty", "Qty")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.poEditor.line.unitCost", "Unit cost")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.poEditor.line.tax", "Tax")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.poEditor.line.lineTotal", "Line total")}
                  </th>
                  <th scope="col" className="p-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const item = supplierItemFor(line.variant_id);
                  const belowMoq =
                    item !== undefined &&
                    line.qty_ordered !== "" &&
                    compareDecimal(line.qty_ordered, item.minimum_order_qty, QTY_SCALE) < 0;
                  const currencyMismatch =
                    item !== undefined && item.currency !== header.currency.toUpperCase();
                  const prefix = `lines[${index}]`;
                  return (
                    <tr key={line.key} className="border-b align-top">
                      <td className="p-2 text-muted-foreground">{index + 1}</td>
                      <td className="min-w-52 p-2">
                        <Combobox
                          aria-label={t(
                            "purchasing.poEditor.line.variantOf",
                            "Variant on line {{n}}",
                            {
                              n: index + 1,
                            },
                          )}
                          options={variantOptions}
                          value={line.variant_id === "" ? null : line.variant_id}
                          onChange={(value) => applySupplierDefaults(line.key, value ?? "")}
                          disabled={!editable}
                          placeholder={t("purchasing.poEditor.line.pickVariant", "Choose a SKU…")}
                          searchPlaceholder={t("purchasing.common.searchSku", "Search SKU…")}
                          emptyMessage={t("purchasing.common.noVariants", "No variants found.")}
                          aria-invalid={fieldErrors[`${prefix}.variant_id`] !== undefined}
                        />
                        {item !== undefined ? (
                          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            <Badge variant="outline">
                              {t("purchasing.poEditor.line.moq", "MOQ")}{" "}
                              <span dir="ltr">{renderQty(item.minimum_order_qty)}</span>
                            </Badge>
                            <Badge variant="outline">
                              {t("purchasing.poEditor.line.pack", "Pack")}{" "}
                              <span dir="ltr">{renderQty(item.pack_size)}</span>
                            </Badge>
                            <Badge variant="outline">
                              {t("purchasing.poEditor.line.lead", "Lead {{days}}d", {
                                days: item.lead_time_days,
                              })}
                            </Badge>
                            {item.last_cost !== undefined ? (
                              <span dir="ltr">
                                {t("purchasing.poEditor.line.lastCost", "Last cost")}{" "}
                                {renderQty(item.last_cost, COST_SCALE)} {item.currency}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        {belowMoq ? (
                          <p role="status" className="mt-1 text-xs text-warning-foreground">
                            {t(
                              "purchasing.poEditor.line.belowMoq",
                              "Below the supplier minimum order quantity.",
                            )}
                          </p>
                        ) : null}
                        {currencyMismatch ? (
                          <p role="status" className="mt-1 text-xs text-destructive">
                            {t(
                              "purchasing.poEditor.line.currencyMismatch",
                              "Supplier item is priced in {{currency}} — no automatic conversion is applied.",
                              { currency: item?.currency },
                            )}
                          </p>
                        ) : null}
                        {fieldErrors[`${prefix}.variant_id`] !== undefined ? (
                          <p role="alert" className="mt-1 text-xs text-destructive">
                            {fieldErrors[`${prefix}.variant_id`]}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2">
                        <Input
                          aria-label={t(
                            "purchasing.poEditor.line.supplierSkuOf",
                            "Supplier SKU on line {{n}}",
                            { n: index + 1 },
                          )}
                          dir="ltr"
                          className="w-32"
                          value={line.supplier_sku}
                          disabled={!editable}
                          onChange={(event) =>
                            updateLine(line.key, { supplier_sku: event.target.value })
                          }
                          aria-invalid={fieldErrors[`${prefix}.supplier_sku`] !== undefined}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={line.purchase_uom_id}
                          onValueChange={(value) =>
                            updateLine(line.key, { purchase_uom_id: value })
                          }
                          disabled={!editable}
                        >
                          <SelectTrigger
                            className="w-24"
                            aria-label={t("purchasing.poEditor.line.uomOf", "UoM on line {{n}}", {
                              n: index + 1,
                            })}
                          >
                            <SelectValue placeholder={uomCode(line.purchase_uom_id)} />
                          </SelectTrigger>
                          <SelectContent>
                            {uoms.map((uom) => (
                              <SelectItem key={uom.id} value={uom.id}>
                                {uom.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-end">
                        <DecimalInput
                          aria-label={t(
                            "purchasing.poEditor.line.qtyOf",
                            "Quantity on line {{n}}",
                            {
                              n: index + 1,
                            },
                          )}
                          className="w-28 text-end"
                          scale={QTY_SCALE}
                          value={line.qty_ordered}
                          disabled={!editable}
                          onChange={(next) => updateLine(line.key, { qty_ordered: next })}
                          aria-invalid={fieldErrors[`${prefix}.qty_ordered`] !== undefined}
                        />
                        {fieldErrors[`${prefix}.qty_ordered`] !== undefined ? (
                          <p role="alert" className="mt-1 text-xs text-destructive">
                            {fieldErrors[`${prefix}.qty_ordered`]}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2 text-end">
                        <DecimalInput
                          aria-label={t(
                            "purchasing.poEditor.line.unitCostOf",
                            "Unit cost on line {{n}}",
                            { n: index + 1 },
                          )}
                          className="w-32 text-end"
                          scale={COST_SCALE}
                          value={line.unit_cost}
                          disabled={!editable}
                          onChange={(next) => updateLine(line.key, { unit_cost: next })}
                          aria-invalid={fieldErrors[`${prefix}.unit_cost`] !== undefined}
                        />
                        {fieldErrors[`${prefix}.unit_cost`] !== undefined ? (
                          <p role="alert" className="mt-1 text-xs text-destructive">
                            {fieldErrors[`${prefix}.unit_cost`]}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2 text-end">
                        <DecimalInput
                          aria-label={t("purchasing.poEditor.line.taxOf", "Tax on line {{n}}", {
                            n: index + 1,
                          })}
                          className="w-28 text-end"
                          scale={AMOUNT_SCALE}
                          value={line.tax_amount}
                          disabled={!editable}
                          onChange={(next) => updateLine(line.key, { tax_amount: next })}
                        />
                      </td>
                      <td className="p-2 text-end">
                        <MoneyDisplay
                          value={{ amount: lineTotal(line), currency: header.currency }}
                        />
                      </td>
                      <td className="p-2 text-end">
                        {editable ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("purchasing.poEditor.line.remove", "Remove line {{n}}", {
                              n: index + 1,
                            })}
                            onClick={() =>
                              setLines((current) =>
                                current.filter((candidate) => candidate.key !== line.key),
                              )
                            }
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {editable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLines((current) => [...current, emptyLine(defaultUomId)])}
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("purchasing.poEditor.addLine", "Add line")}
              </Button>
            ) : (
              <span />
            )}
            <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground">
                  {t("purchasing.poEditor.subtotalPreview", "Subtotal (preview)")}
                </dt>
                <dd data-testid="po-subtotal-preview">
                  <MoneyDisplay value={{ amount: previewSubtotal, currency: header.currency }} />
                </dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground">
                  {t("purchasing.poEditor.grandTotalPreview", "Grand total (preview)")}
                </dt>
                <dd data-testid="po-grand-total-preview" className="font-medium">
                  <MoneyDisplay value={{ amount: previewGrandTotal, currency: header.currency }} />
                </dd>
              </div>
            </dl>
          </div>
        </TabsContent>

        {!isNew && canReadTransmissions ? (
          <TabsContent value="transmissions" className="flex flex-col gap-3">
            {transmissions.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("purchasing.poEditor.transmissionsEmpty", "Not yet sent.")}
              </p>
            ) : (
              <ul className="flex flex-col divide-y">
                {transmissions.rows.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <Badge variant="outline">
                      {t("purchasing.poEditor.attempt", "Attempt {{n}}", { n: row.attempt_number })}
                    </Badge>
                    <TransmissionStatusBadge status={row.status} />
                    <span dir="ltr" className="font-mono text-xs">
                      {row.recipient_email_masked}
                    </span>
                    <span className="text-muted-foreground">{row.requested_at}</span>
                    {row.failure_code !== undefined ? (
                      <span className="text-destructive">
                        {enumLabel(t, "transmissionFailure", row.failure_code)}
                      </span>
                    ) : null}
                    <a
                      className="text-primary underline-offset-4 hover:underline"
                      href={row.document_download_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("purchasing.poEditor.openDocument", "Open document")}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        ) : null}
      </Tabs>

      {!isNew ? (
        <div>
          <Separator className="my-2" />
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => onOpenGrns(purchaseOrderId as string)}
          >
            {t("purchasing.poEditor.viewReceipts", "View goods receipts for this order")}
            <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden="true" />
          </Button>
        </div>
      ) : null}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("purchasing.poEditor.sendTitle", "Send to supplier")}</DialogTitle>
            <DialogDescription>
              {t(
                "purchasing.poEditor.sendBody",
                "The rendered purchase order is emailed to the supplier. Leave the recipient blank to use the supplier's default purchasing contact.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-po-recipient">
              {t("purchasing.poEditor.recipient", "Recipient email")}
            </Label>
            <Input
              id="purchasing-po-recipient"
              type="email"
              dir="ltr"
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
              aria-invalid={fieldErrors.recipient_email !== undefined}
            />
            {fieldErrors.recipient_email !== undefined ? (
              <p role="alert" className="text-xs text-destructive">
                {fieldErrors.recipient_email}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSendOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={sendMutation.isPending}
              disabled={sendMutation.isPending}
              onClick={() =>
                sendMutation.mutate(recipient === "" ? {} : { recipient_email: recipient })
              }
            >
              {t("purchasing.poEditor.confirmSend", "Send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("purchasing.poEditor.closeTitle", "Close purchase order")}</DialogTitle>
            <DialogDescription>
              {t(
                "purchasing.poEditor.closeBody",
                "Closing is available once the order is fully received. A closed order accepts no further receipts.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCloseOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={closeMutation.isPending}
              disabled={closeMutation.isPending}
              onClick={() => closeMutation.mutate()}
            >
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reapprovalNotice !== null}
        onOpenChange={(open) => {
          if (!open) setReapprovalNotice(null);
        }}
      >
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>
              {t("purchasing.poEditor.reapprovalTitle", "Returned to pending approval")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "purchasing.poEditor.reapprovalBody",
                "The saved total moved beyond the configured tolerance, so the approval was cleared and approvers were notified.",
              )}
            </DialogDescription>
          </DialogHeader>
          {reapprovalNotice !== null ? (
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <MoneyDisplay value={{ amount: reapprovalNotice.from, currency: header.currency }} />
              <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden="true" />
              <MoneyDisplay value={{ amount: reapprovalNotice.to, currency: header.currency }} />
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" onClick={() => setReapprovalNotice(null)}>
              {t("actions.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          void detail.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}

/** Exported for the route factory's status-driven affordances. */
export function isEditableStatus(status: PurchaseOrder["status"]): boolean {
  return EDITABLE_STATUSES.has(status);
}
